import React from "react"
import * as Styles from "./SurveysPage.styles"
import * as Mui from "@material-ui/core"
import { t } from "ttag"
import { IconButton, Illustration } from "@humanpredictiveintelligence/myqvt-library"
import { useHistory, useParams } from "react-router-dom"

import { APPLICATION_URL } from "features/Navigation"
import { useUserPermissions } from "features/Permissions"
import { UserPermissionCode } from "models/generated"
import resultsIllustration from "assets/images/illustration_results.png"
import { SurveyForm, SurveyList } from "../../components"

import { useGetCustomSurveysQuery } from "graphql/queries/generated/GetCustomSurveys"

export const SurveysPage = () => {
  const theme = Mui.useTheme()
  const isCollapsed = !Mui.useMediaQuery(theme.breakpoints.up("landscape"))
  const isUserAllowed = useUserPermissions()
  const isAllowedToCreateSurvey = isUserAllowed(UserPermissionCode.SurveyCreate)
  const history = useHistory()
  const params = useParams<SurveysPageParams>()

  const action = params.action
  const selectedSurveyId = params.surveyId
    ? parseInt(params.surveyId, 10)
    : undefined

  const isActionSelected = action === "new" || selectedSurveyId

  const {
    data: customSurveysData,
    loading: areCustomSurveysLoading,
    refetch: refetchCustomSurveys,
  } = useGetCustomSurveysQuery({
    notifyOnNetworkStatusChange: true,
  })

  return (
    <Styles.Container data-testid="SurveysPage">
      <Styles.SurveysList $isCollapsed={isCollapsed}>
        <Styles.SurveysListHeaderContainer>
          <div>
            <Styles.SurveysListTitle>{t`My surveys`}</Styles.SurveysListTitle>
            <IconButton
              icon="checklist"
              size={isCollapsed ? "extrabig" : "big"}
              link={APPLICATION_URL.newSurvey()}
              isDisabled={!isAllowedToCreateSurvey}
              isAllowed={isAllowedToCreateSurvey}
              tooltip={!isAllowedToCreateSurvey
                ? t`You don't have sufficient rights to perform this action`
                : isCollapsed ? t`Create a survey` : undefined
              }
              tooltipPlacement="top"
            >
              {t`Create a survey`}
            </IconButton>
          </div>
        </Styles.SurveysListHeaderContainer>
        <SurveyList
          isCollapsed={isCollapsed}
          isLoading={areCustomSurveysLoading}
          surveys={customSurveysData?.getCustomSurveys}
          activeSurveyId={selectedSurveyId}
        />
      </Styles.SurveysList>

      <Styles.SurveyDetails $isEmpty={!isActionSelected}>
        {isActionSelected
          ? (
            <SurveyForm
              surveyId={selectedSurveyId}
              onSurveyCUD={onSurveyCUD}
              onCancelRequested={() => history.push(APPLICATION_URL.speakupSurveys())}
            />
          )
          : (
            <Illustration
              illustration={resultsIllustration}
              title={t`You have not selected any survey yet...`}
            >
              {t`You can select a survey to update or create a new one.`}
            </Illustration>
          )
        }
      </Styles.SurveyDetails>
    </Styles.Container>
  )

  async function onSurveyCUD() {
    history.push(APPLICATION_URL.speakupSurveys())
    await refetchCustomSurveys()
  }
}

interface SurveysPageParams {
  surveyId?: string,
  action?: string,
}
